import { combineReducers, applyMiddleware, createStore, compose } from 'redux';
import thunk from 'redux-thunk';
import serverVars from 'server-vars';
import { clientIntlPolyfill } from 'dibs-intl/exports/clientIntlPolyfill';
import { VisitorTracking } from 'dibs-visit-tracking/exports/visitorTracking';
import { isMasqueradingAsBuyer, cookieJarConstants } from 'dibs-cookie-jar';
import Cookies from 'js-cookie';

import { getBuyerHeaderClient } from './header';
import { getBuyerFooterClient } from './footer';
import { getBuyerCookieNotificationClient } from './cookieNotification';
import initializeTracking from '../utils/initializeTracking';
import { addLoginHandlers } from '../utils/loginHandlers';
import dblReducers from '../reducers';
import { actionCreators } from '../actions/index';
import createRelayEnvironment from '../relayClientEnvironment';
import AuthModalCheck from '../utils/AuthModalCheck';
import isPageType from '../utils/isPageType';
import abExperiments from '../utils/abExperiments';
import pageTypes from '../utils/pageConstants';
import scriptInjector from '../utils/scriptInjector';
import { loadWidgetButtons } from '../utils/pageWidgetButtons';
import contactModal from '../contactModal/async';

import '../scss/baseBuyerStyles.scss';

const { USER, MASQUERADE_BUYER, USER_EMAIL, USER_TYPE, REAL_USER_TYPE } = cookieJarConstants;

const dblMiddleware = [thunk];

// will be used to store the queue so that functions exported from dbl can interact with it
let queue;

function createGlobalStore(reducers, middlewares, state) {
    const reducer = combineReducers({ ...reducers, ...dblReducers });
    const middleware = middlewares
        .concat(dblMiddleware)
        .filter((value, index, self) => self.indexOf(value) === index);

    let enhancer;
    if (process.env.NODE_ENV !== 'production') {
        const { composeWithDevTools } = require('redux-devtools-extension');
        enhancer = composeWithDevTools;
    } else {
        enhancer = compose;
    }

    return createStore(reducer, state, enhancer(applyMiddleware(...middleware)));
}

Cookies.defaults = {
    domain: serverVars.get('cookieDomain'),
    expires: 182,
    path: '/',
};

function logoutFunc() {
    if (isMasqueradingAsBuyer(document.cookie)) {
        Cookies.remove(MASQUERADE_BUYER);
        /*
         * TRADE-3112
         *
         * The USER_TYPE cookie is handled differently than the masquerade
         * cookie -- the real user's type is stored in REAL_USER_TYPE, and
         * the masquerade user's type is used to set USER_TYPE.
         *
         * On ending a masqueradee session, read the REAL_USER_TYPE cookie
         * and, if present, use that to re-populate the USER_TYPE cookie.
         */
        const realUserType = Cookies.get(REAL_USER_TYPE);
        if (realUserType) {
            Cookies.remove(USER_TYPE);
            Cookies.remove(REAL_USER_TYPE);
            Cookies.set(USER_TYPE, realUserType);
        }
    } else {
        Cookies.remove(USER);
        Cookies.remove(USER_EMAIL);
        Cookies.remove(USER_TYPE);
    }
    location.reload();
}

function initVisitorTracking() {
    new VisitorTracking();
}

/**
 * @param {object} options
 */
function _initialize(options = {}) {
    const { reducers = {}, middleware = [], logoutUser = logoutFunc, state = {} } = options;

    if (
        state.hasOwnProperty('cart') ||
        state.hasOwnProperty('header') ||
        state.hasOwnProperty('currency')
    ) {
        throw new Error(`state cannot include reserved keys: cart, header, currency`);
    }

    // clone data with stringify/parse to ensure relay does not delete the object references when
    // passed to multiple relay environments
    const data = JSON.parse(
        JSON.stringify(serverVars.get('dbl.relayData')) ||
            serverVars.get('headerRelayPreloadedData')
    );
    const relayEnv = createRelayEnvironment(data);
    const { header, cart } = serverVars.get('header') || {};
    const { currency } = serverVars.get('footer') || {};
    header.logoutUser = logoutUser;

    const store = createGlobalStore(reducers, middleware, { header, currency, cart, ...state });
    const actions = {
        reinitTracking: () => {
            store.dispatch(actionCreators.resetTrackingFired());
        },
    };

    getBuyerHeaderClient({
        node: document.getElementById('js-header'),
        store,
        relayEnv,
    });
    getBuyerFooterClient({
        node: document.getElementById('js-footer'),
        store,
        relayEnv,
    });
    getBuyerCookieNotificationClient({
        node: document.getElementById('js-cookie-notification'),
        relayEnv,
    });

    AuthModalCheck.init();

    initVisitorTracking();

    return { store, relayEnv, actions };
}

function _initializeResponsive(options = {}, NotificationQueue) {
    const { notifications = [], autoRunNotificationQueue = true, ...otherOptions } = options;
    const { store, actions, relayEnv } = _initialize(otherOptions);
    const isTextToTopEnabled = serverVars.get('enablers.isTextToTopEnabled');
    loadWidgetButtons(isTextToTopEnabled);
    queue = new NotificationQueue(notifications, store, relayEnv, autoRunNotificationQueue);
    return { store, actions };
}

function initializeDBL(options = {}) {
    const { initializers = [], ...otherOptions } = options;

    const localeDataKey = serverVars.get('localeDataKey') || 'en';
    initializers.push(clientIntlPolyfill(localeDataKey));

    const queuePromise = import(/* webpackChunkName: "NotificationQueue" */ '../notificationQueue/Queue');

    return Promise.all([queuePromise, ...initializers]).then(
        ([{ default: NotificationQueue }, ...values]) => {
            const { store, actions } = _initializeResponsive(otherOptions, NotificationQueue);
            const publicQueue = {
                _getNotificationQueue() {
                    return queue._getNotificationQueue();
                },
                runNotificationQueue() {
                    // Increases visit counter before re-run of queue.
                    initVisitorTracking();
                    queue.runQueue();
                },
            };

            return {
                values,
                store,
                queue: publicQueue,
                actions,
            };
        }
    );
}

function initializeDBLMobile(options = {}) {
    const { initializers = [], ...otherOptions } = options;

    const localeDataKey = serverVars.get('localeDataKey') || 'en';
    initializers.push(clientIntlPolyfill(localeDataKey));

    return Promise.all(initializers).then(values => {
        const { store, actions } = _initialize(otherOptions);
        return { values, store, actions };
    });
}

function _getNotificationQueue() {
    return queue._getNotificationQueue();
}

function runNotificationQueue() {
    queue.runQueue();
}

// used only for testing notification order
export {
    abExperiments,
    addLoginHandlers,
    contactModal,
    initializeTracking,
    initializeDBL,
    initializeDBLMobile,
    isPageType,
    pageTypes,
    scriptInjector,
    _getNotificationQueue,
    runNotificationQueue,
};
